import React, { useState, useEffect } from 'react';
import { Modal, Select, message, Alert, Typography, Space, Spin, Tag, Empty, Button, Card, Input, InputNumber, Collapse, Divider, Segmented } from 'antd';
import { LinkOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ThunderboltOutlined, SoundOutlined, EditOutlined, BulbOutlined, SaveOutlined, FileTextOutlined } from '@ant-design/icons';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { filmApi } from '../../services/api/film';
import type { SourceStory } from '../../types';
import type { FilmPlan } from '../../types/film';

const { Text, Link } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ScriptLine {
  timeframe: string;
  action: string;
}

interface AnalysisItem {
  timeframe: string;
  name: string;
  description: string;
  flow: string;
  instruments: string[];
  genre: string;
  moodAndTone: string[];
  tempo: number;
  layering: string;
  duration: number;
  scriptLines: ScriptLine[];
  reasoning: string;
}

interface GeneratedAudioData {
  url: string;
  name: string;
  description: string | null;
  duration: number;
  fileSize: number;
  format: string;
  bpm: number;
  mood: string[];
  flow: string | null;
  instruments: string[];
  genre: string;
  layering: string;
  savedId?: string;
}

interface PlanValidation {
  loading: boolean;
  ready: boolean;
  totalEvents: number;
  readyEvents: number;
  notReadyEvents: number;
  message: string;
}

interface GenerateFromFilmModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GenerateFromFilmModal: React.FC<GenerateFromFilmModalProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [stories, setStories] = useState<SourceStory[]>([]);
  const [loadingStories, setLoadingStories] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState<string | undefined>();
  const [plans, setPlans] = useState<FilmPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>();
  const [validation, setValidation] = useState<PlanValidation | null>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [analysisItems, setAnalysisItems] = useState<AnalysisItem[]>([]);
  const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
  const [generatedAudios, setGeneratedAudios] = useState<Map<number, GeneratedAudioData>>(new Map());
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const [itemModes, setItemModes] = useState<Map<number, 'basic' | 'advanced' | 'prompt'>>(new Map());
  const [itemRawPrompts, setItemRawPrompts] = useState<Map<number, string>>(new Map());
  const [itemPrompts, setItemPrompts] = useState<Map<number, string>>(new Map());
  const [parsingIndex, setParsingIndex] = useState<number | null>(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (visible) {
      loadStories();
    } else {
      resetAll();
    }
  }, [visible]);

  useEffect(() => {
    if (selectedStoryId) {
      loadPlans(selectedStoryId);
      setSelectedPlanId(undefined);
      setValidation(null);
      setAnalysisItems([]);
      setGeneratedAudios(new Map());
    } else {
      setPlans([]);
      setSelectedPlanId(undefined);
      setValidation(null);
      setAnalysisItems([]);
    }
  }, [selectedStoryId]);

  useEffect(() => {
    if (selectedPlanId) {
      validatePlan(selectedPlanId);
      setAnalysisItems([]);
      setGeneratedAudios(new Map());
    } else {
      setValidation(null);
      setAnalysisItems([]);
    }
  }, [selectedPlanId]);

  const resetAll = () => {
    setSelectedStoryId(undefined);
    setSelectedPlanId(undefined);
    setPlans([]);
    setValidation(null);
    setAnalysisItems([]);
    setGeneratedAudios(new Map());
    setItemModes(new Map());
    setItemPrompts(new Map());
    setItemRawPrompts(new Map());
    setParsingIndex(null);
    setGeneratingAll(false);
    setSavingIndex(null);
    setSavingAll(false);
    if (audioEl) { audioEl.pause(); }
    setPlayingUrl(null);
  };

  const loadStories = async () => {
    try {
      setLoadingStories(true);
      const result = await sourceStoriesApi.getSourceStories({ pageSize: 200 });
      const all = [...(result.pinned || []), ...(result.data || [])];
      const unique = Array.from(new Map(all.map(s => [s.id, s])).values());
      setStories(unique);
    } catch (error: any) {
      message.error('Failed to load stories: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingStories(false);
    }
  };

  const loadPlans = async (storyId: string) => {
    try {
      setLoadingPlans(true);
      const result = await filmApi.listPlans(storyId);
      setPlans(Array.isArray(result) ? result : (result as any).data || []);
    } catch (error: any) {
      message.error('Failed to load plans: ' + (error.message || 'Unknown error'));
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const validatePlan = async (planId: string) => {
    try {
      setValidation({ loading: true, ready: false, totalEvents: 0, readyEvents: 0, notReadyEvents: 0, message: '' });
      const result = await audioLibraryApi.validateFilmPlan(planId);
      const data = result.data || result;
      setValidation({ loading: false, ready: data.ready, totalEvents: data.totalEvents, readyEvents: data.readyEvents, notReadyEvents: data.notReadyEvents, message: data.message });
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Validation failed';
      setValidation({ loading: false, ready: false, totalEvents: 0, readyEvents: 0, notReadyEvents: 0, message: msg });
    }
  };

  const handleAnalyze = async () => {
    if (!selectedPlanId) return;
    try {
      setAnalyzing(true);
      const result = await audioLibraryApi.analyzeFilmPlan(selectedPlanId);
      const rawItems = result.data || result;
      const items = (Array.isArray(rawItems) ? rawItems : []).map((item: any) => ({
        ...item,
        duration: item.duration || 60,
        moodAndTone: Array.isArray(item.moodAndTone) ? item.moodAndTone : item.moodAndTone ? [item.moodAndTone] : [],
        scriptLines: Array.isArray(item.scriptLines) ? item.scriptLines : [],
      }));
      setAnalysisItems(items);
      setGeneratedAudios(new Map());
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to analyze: ' + errorMsg);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateItem = async (index: number) => {
    const item = analysisItems[index];
    if (!item) return;

    try {
      setGeneratingIndex(index);

      const existingAudio = generatedAudios.get(index);
      if (existingAudio?.savedId) {
        try {
          await audioLibraryApi.deleteAudio(existingAudio.savedId);
        } catch (delErr: any) {
          console.warn('Failed to delete old saved audio:', delErr.message);
        }
      }

      const mode = itemModes.get(index) || 'advanced';
      const isPromptMode = mode === 'prompt';
      const rawPrompt = isPromptMode ? (itemRawPrompts.get(index) || buildPromptFromItem(item)) : undefined;

      const result = await audioLibraryApi.generateFromProperties({
        name: item.name,
        description: item.description || undefined,
        instruments: item.instruments,
        genre: item.genre,
        moodAndTone: item.moodAndTone,
        tempo: item.tempo,
        layering: item.layering,
        flow: item.flow || undefined,
        duration: item.duration || 60,
        draft: true,
        ...(isPromptMode && rawPrompt ? { prompt: rawPrompt } : {}),
      });
      const audio = result.data || result;
      setGeneratedAudios(prev => new Map(prev).set(index, {
        url: audio.url,
        name: audio.name,
        description: audio.description,
        duration: audio.duration,
        fileSize: audio.fileSize,
        format: audio.format,
        bpm: audio.bpm,
        mood: audio.mood,
        flow: audio.flow,
        instruments: audio.instruments,
        genre: audio.genre,
        layering: audio.layering,
      }));
      message.success(`Audio "${item.name}" generated! Click Save to add to library.`);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to generate: ' + errorMsg);
    } finally {
      setGeneratingIndex(null);
    }
  };

  const handlePlay = (url: string) => {
    if (playingUrl === url && audioEl && !audioEl.paused) {
      audioEl.pause();
      setPlayingUrl(null);
    } else {
      if (audioEl) audioEl.pause();
      const el = new Audio(url);
      el.play();
      setAudioEl(el);
      setPlayingUrl(url);
      el.onended = () => setPlayingUrl(null);
    }
  };

  const updateItem = (index: number, field: keyof AnalysisItem, value: any) => {
    setAnalysisItems(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const buildPromptFromItem = (item: AnalysisItem): string => {
    const durationSeconds = item.duration || 60;
    const useClipModel = durationSeconds <= 30;
    let prompt = '';
    if (!useClipModel) {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      const durationStr = minutes > 0 && seconds > 0 ? `${minutes}m${seconds}s` : minutes > 0 ? `${minutes}m` : `${seconds}s`;
      prompt += `Create a ${durationStr} instrumental only music (no vocals). `;
    } else {
      prompt += `Create instrumental only music (no vocals). `;
    }
    prompt += `${item.description ? item.description + '. ' : ''}Instruments: ${(item.instruments || []).join(', ')}; Genre: ${item.genre || ''}; Mood and tone: ${(item.moodAndTone || []).join(', ')}; Tempo: ${item.tempo || 120}; Layering: ${item.layering || ''}${item.flow ? `; Flow: ${item.flow}` : ''}`;
    return prompt;
  };

  const handleParseItemPrompt = async (index: number) => {
    const prompt = itemPrompts.get(index);
    if (!prompt?.trim()) {
      message.warning('Vui lòng nhập mô tả nhạc bạn muốn tạo');
      return;
    }
    try {
      setParsingIndex(index);
      const result = await audioLibraryApi.parseAudioPrompt(prompt);
      const data = result.data || result;
      const fields: (keyof AnalysisItem)[] = ['name', 'description', 'instruments', 'genre', 'moodAndTone', 'tempo', 'layering', 'flow'];
      for (const field of fields) {
        if (data[field] !== undefined) {
          updateItem(index, field, data[field]);
        }
      }
      if (data.duration) {
        updateItem(index, 'duration', data.duration);
      }
      setItemModes(prev => new Map(prev).set(index, 'advanced'));
      message.success('AI đã phân tích và điền vào form. Bạn có thể chỉnh sửa trước khi Generate.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to parse prompt: ' + errorMsg);
    } finally {
      setParsingIndex(null);
    }
  };

  const handleReanalyzeFromReasoning = async (index: number) => {
    const item = analysisItems[index];
    const reasoning = item?.reasoning;
    if (!reasoning?.trim()) {
      message.warning('Không có reasoning để phân tích. Hãy nhập mô tả bạn muốn.');
      return;
    }
    if (!selectedPlanId) {
      message.warning('Không tìm thấy plan. Vui lòng thử lại.');
      return;
    }
    try {
      setParsingIndex(index);
      const result = await audioLibraryApi.reanalyzeSegment(selectedPlanId, item.timeframe, reasoning);
      const data = result.data || result;
      const fields: (keyof AnalysisItem)[] = ['name', 'description', 'instruments', 'genre', 'moodAndTone', 'tempo', 'layering', 'flow'];
      for (const field of fields) {
        if (data[field] !== undefined) {
          updateItem(index, field, data[field]);
        }
      }
      if (data.duration) {
        updateItem(index, 'duration', data.duration);
      }
      message.success('AI đã phân tích lại dựa trên script và cập nhật form. Bạn có thể chỉnh sửa trước khi Generate.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to re-analyze: ' + errorMsg);
    } finally {
      setParsingIndex(null);
    }
  };

  const handleGenerateAll = async () => {
    const pendingIndices = analysisItems
      .map((_, i) => i)
      .filter(i => {
        const mode = itemModes.get(i) || 'advanced';
        if (mode === 'basic') return false;
        if (mode === 'prompt' && !(itemRawPrompts.get(i) || '').trim()) return false;
        return !generatedAudios.has(i);
      });

    if (pendingIndices.length === 0) {
      message.info('Tất cả segment đã được generate hoặc đang ở chế độ Basic.');
      return;
    }

    setGeneratingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const index of pendingIndices) {
      const item = analysisItems[index];
      if (!item) continue;

      try {
        setGeneratingIndex(index);
        const mode = itemModes.get(index) || 'advanced';
        const isPromptMode = mode === 'prompt';
        const rawPrompt = isPromptMode ? (itemRawPrompts.get(index) || buildPromptFromItem(item)) : undefined;

        const result = await audioLibraryApi.generateFromProperties({
          name: item.name,
          description: item.description || undefined,
          instruments: item.instruments,
          genre: item.genre,
          moodAndTone: item.moodAndTone,
          tempo: item.tempo,
          layering: item.layering,
          flow: item.flow || undefined,
          duration: item.duration || 60,
          draft: true,
          ...(isPromptMode && rawPrompt ? { prompt: rawPrompt } : {}),
        });
        const audio = result.data || result;
        setGeneratedAudios(prev => new Map(prev).set(index, {
          url: audio.url,
          name: audio.name,
          description: audio.description,
          duration: audio.duration,
          fileSize: audio.fileSize,
          format: audio.format,
          bpm: audio.bpm,
          mood: audio.mood,
          flow: audio.flow,
          instruments: audio.instruments,
          genre: audio.genre,
          layering: audio.layering,
        }));
        successCount++;
      } catch (error: any) {
        failCount++;
        const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
        message.error(`Failed to generate "${item.name}": ${errorMsg}`);
      } finally {
        setGeneratingIndex(null);
      }
    }

    setGeneratingAll(false);
    if (successCount > 0) {
      message.success(`Generated ${successCount} audio(s)${failCount > 0 ? `, ${failCount} failed` : ''}. Click Save to add to library.`);
    }
  };

  const buildDownloadFilename = (index: number, format: string) => {
    const storyName = stories.find(s => s.id === selectedStoryId)?.name || 'story';
    const planName = selectedPlan?.name || `plan-${selectedPlanId?.slice(0, 8)}`;
    const item = analysisItems[index];
    const songName = item?.name || 'audio';
    const timeframe = (item?.timeframe || '').replace(/\s/g, '');
    return `${storyName}_${planName}_${songName}_${timeframe}.${format || 'wav'}`;
  };

  const handleSaveItem = async (index: number) => {
    const generated = generatedAudios.get(index);
    if (!generated || generated.savedId) return;

    try {
      setSavingIndex(index);
      const result = await audioLibraryApi.saveGeneratedAudio({
        url: generated.url,
        name: generated.name,
        description: generated.description || undefined,
        duration: generated.duration,
        fileSize: generated.fileSize,
        format: generated.format,
        bpm: generated.bpm,
        mood: generated.mood,
        flow: generated.flow || undefined,
        instruments: generated.instruments,
        genre: generated.genre,
        layering: generated.layering,
      });
      const saved = result.data || result;
      setGeneratedAudios(prev => {
        const copy = new Map(prev);
        copy.set(index, { ...generated, savedId: saved.id });
        return copy;
      });
      message.success(`"${generated.name}" saved to library!`);
      const filename = buildDownloadFilename(index, generated.format);
      audioLibraryApi.download(saved.id, filename).catch(() => {
        message.error('Saved but failed to download');
      });
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to save: ' + errorMsg);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleSaveAll = async () => {
    const unsavedIndices = Array.from(generatedAudios.entries())
      .filter(([_, data]) => !data.savedId)
      .map(([i]) => i);

    if (unsavedIndices.length === 0) {
      message.info('Tất cả audio đã được lưu.');
      return;
    }

    setSavingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const index of unsavedIndices) {
      const generated = generatedAudios.get(index);
      if (!generated) continue;

      try {
        setSavingIndex(index);
        const result = await audioLibraryApi.saveGeneratedAudio({
          url: generated.url,
          name: generated.name,
          description: generated.description || undefined,
          duration: generated.duration,
          fileSize: generated.fileSize,
          format: generated.format,
          bpm: generated.bpm,
          mood: generated.mood,
          flow: generated.flow || undefined,
          instruments: generated.instruments,
          genre: generated.genre,
          layering: generated.layering,
        });
        const saved = result.data || result;
        setGeneratedAudios(prev => {
          const copy = new Map(prev);
          copy.set(index, { ...generated, savedId: saved.id });
          return copy;
        });
        successCount++;
        const filename = buildDownloadFilename(index, generated.format);
        audioLibraryApi.download(saved.id, filename).catch(() => {});
      } catch (error: any) {
        failCount++;
        const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
        message.error(`Failed to save "${generated.name}": ${errorMsg}`);
      } finally {
        setSavingIndex(null);
      }
    }

    setSavingAll(false);
    if (successCount > 0) {
      message.success(`Saved ${successCount} audio(s) to library${failCount > 0 ? `, ${failCount} failed` : ''}.`);
    }
  };

  const hasSavedAny = Array.from(generatedAudios.values()).some(d => !!d.savedId);

  const handleClose = () => {
    if (audioEl) audioEl.pause();
    if (hasSavedAny) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const canAnalyze = selectedPlanId && validation && !validation.loading && validation.ready;

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = { DRAFT: 'default', PLAN_APPROVED: 'blue', FRAMES_PENDING: 'orange', FRAMES_APPROVED: 'cyan', VIDEO_PENDING: 'gold', VIDEO_APPROVED: 'green', ASSEMBLED: 'purple' };
    return colors[status] || 'default';
  };

  return (
    <Modal
      title="Generate Audio from Film"
      open={visible}
      onCancel={handleClose}
      width={analysisItems.length > 0 ? 900 : 600}
      footer={analysisItems.length > 0 ? [
        <Button key="close" onClick={handleClose}>
          {hasSavedAny ? 'Done' : 'Cancel'}
        </Button>
      ] : undefined}
      okText="Analyze Script"
      onOk={handleAnalyze}
      confirmLoading={analyzing}
      okButtonProps={{ disabled: !canAnalyze, style: analysisItems.length > 0 ? { display: 'none' } : undefined }}
      cancelButtonProps={{ style: analysisItems.length > 0 ? { display: 'none' } : undefined }}
    >
      {analysisItems.length === 0 ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>1. Select Story</Text>
            <Select
              placeholder="Search and select a story..."
              style={{ width: '100%' }}
              value={selectedStoryId}
              onChange={setSelectedStoryId}
              loading={loadingStories}
              showSearch
              filterOption={(input, option) => (option?.label as string || '').toLowerCase().includes(input.toLowerCase())}
              options={stories.map(s => ({ value: s.id, label: s.name }))}
              notFoundContent={loadingStories ? <Spin size="small" /> : <Empty description="No stories" />}
            />
            {selectedStoryId && (
              <Link href={`/source-stories/${selectedStoryId}`} target="_blank" style={{ fontSize: 12, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <LinkOutlined /> Open story detail
              </Link>
            )}
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>2. Select Plan</Text>
            <Select
              placeholder={selectedStoryId ? "Select a film plan..." : "Select a story first"}
              style={{ width: '100%' }}
              value={selectedPlanId}
              onChange={setSelectedPlanId}
              loading={loadingPlans}
              disabled={!selectedStoryId}
              notFoundContent={loadingPlans ? <Spin size="small" /> : !selectedStoryId ? null : <Empty description="No plans found" />}
            >
              {plans.map(plan => (
                <Option key={plan.id} value={plan.id}>
                  <Space>
                    <span>{plan.name || `Plan ${plan.id.slice(0, 8)}`}</span>
                    <Tag color={getStatusColor(plan.status)}>{plan.status}</Tag>
                  </Space>
                </Option>
              ))}
            </Select>
            {selectedPlanId && selectedPlan?.logline && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f5f5f5', borderRadius: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}><strong>Logline:</strong> {selectedPlan.logline}</Text>
              </div>
            )}
          </div>

          {validation && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>3. Validation</Text>
              {validation.loading ? (
                <Space><LoadingOutlined /><Text type="secondary">Validating plan...</Text></Space>
              ) : validation.ready ? (
                <Alert type="success" showIcon icon={<CheckCircleOutlined />} message="Ready to analyze" description={`All ${validation.totalEvents} event(s) have completed video clips.`} />
              ) : (
                <Alert type="error" showIcon icon={<CloseCircleOutlined />} message="Not ready" description={validation.message} />
              )}
            </div>
          )}
        </Space>
      ) : (
        <div>
          <Alert
            type="info"
            showIcon
            message={`${analysisItems.length} audio segment(s) analyzed from script`}
            description="Edit properties, Generate to preview, then Save to add to library."
            style={{ marginBottom: 16 }}
          />

          {analysisItems.some(item => item.scriptLines?.length > 0) && (
            <Collapse size="small" style={{ marginBottom: 16 }}>
              <Collapse.Panel
                key="script"
                header={
                  <Text strong style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FileTextOutlined /> Full Script Timeline
                  </Text>
                }
              >
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {analysisItems.flatMap(item => item.scriptLines || [])
                    .filter((line, i, arr) => arr.findIndex(l => l.timeframe === line.timeframe && l.action === line.action) === i)
                    .map((line, li) => (
                      <div key={li} style={{ fontSize: 12, lineHeight: 1.8, color: '#333' }}>
                        <Tag color="geekblue" style={{ fontSize: 10, marginRight: 6 }}>{line.timeframe}</Tag>
                        <Text style={{ fontSize: 12 }}>{line.action}</Text>
                      </div>
                    ))}
                </div>
              </Collapse.Panel>
            </Collapse>
          )}

          <Collapse
            defaultActiveKey={analysisItems.map((_, i) => String(i))}
            style={{ maxHeight: 600, overflow: 'auto' }}
          >
            {analysisItems.map((item, index) => {
              const generated = generatedAudios.get(index);
              const itemMode = itemModes.get(index) || 'advanced';
              const isBasic = itemMode === 'basic';
              const isPrompt = itemMode === 'prompt';
              return (
                <Collapse.Panel
                  key={String(index)}
                  header={
                    <Space>
                      <Tag color="blue">{item.timeframe}</Tag>
                      <span>{item.name}</span>
                      {generated && !generated.savedId && <Tag color="orange">Generated</Tag>}
                      {generated?.savedId && <Tag color="success">Saved</Tag>}
                    </Space>
                  }
                  extra={
                    <Space onClick={e => e.stopPropagation()}>
                      {generated && (
                        <Button
                          size="small"
                          icon={<SoundOutlined />}
                          type={playingUrl === generated.url ? 'primary' : 'default'}
                          onClick={() => handlePlay(generated.url)}
                        >
                          {playingUrl === generated.url ? 'Stop' : 'Play'}
                        </Button>
                      )}
                      {generated && !generated.savedId && (
                        <Button
                          size="small"
                          icon={<SaveOutlined />}
                          loading={savingIndex === index}
                          disabled={savingAll && savingIndex !== index}
                          onClick={() => handleSaveItem(index)}
                        >
                          Save
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={generatingIndex === index}
                        disabled={isBasic || (isPrompt && !(itemRawPrompts.get(index) || '').trim()) || (generatingAll && generatingIndex !== index) || savingAll}
                        onClick={() => handleGenerateItem(index)}
                      >
                        {generated ? 'Regenerate' : 'Generate'}
                      </Button>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div style={{ padding: '8px 12px', background: '#f9f0ff', borderRadius: 6, border: '1px solid #d3adf7' }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Timeframe</Text>
                        <Input size="small" value={item.timeframe} onChange={e => updateItem(index, 'timeframe', e.target.value)} placeholder="MM:SS - MM:SS" />
                      </div>
                      <Text strong style={{ fontSize: 12, color: '#722ed1', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                        <BulbOutlined /> AI Reasoning
                      </Text>
                      <TextArea
                        size="small"
                        rows={3}
                        value={item.reasoning || ''}
                        onChange={e => updateItem(index, 'reasoning', e.target.value)}
                        style={{ fontSize: 12, background: '#fff', border: '1px solid #d9d9d9' }}
                        placeholder="AI giải thích lý do chọn các thuộc tính nhạc. Bạn có thể sửa lại rồi bấm Re-analyze để AI tạo lại thuộc tính phù hợp hơn."
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<BulbOutlined />}
                        loading={parsingIndex === index}
                        onClick={() => handleReanalyzeFromReasoning(index)}
                        style={{ marginTop: 8, background: '#722ed1', borderColor: '#722ed1' }}
                      >
                        Re-analyze with AI
                      </Button>
                    </div>

                    <Segmented
                      size="small"
                      value={itemMode}
                      onChange={(val) => {
                        const mode = val as 'basic' | 'advanced' | 'prompt';
                        setItemModes(prev => new Map(prev).set(index, mode));
                        if (mode === 'prompt' && !itemRawPrompts.has(index)) {
                          setItemRawPrompts(prev => new Map(prev).set(index, buildPromptFromItem(item)));
                        }
                      }}
                      options={[
                        { label: 'Basic', value: 'basic' },
                        { label: 'Advanced', value: 'advanced' },
                        { label: 'Prompt', value: 'prompt' },
                      ]}
                    />

                    {isBasic && (
                      <div style={{ padding: 12, background: '#f6f8fa', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                        <TextArea
                          size="small"
                          rows={3}
                          value={itemPrompts.get(index) || ''}
                          onChange={e => setItemPrompts(prev => new Map(prev).set(index, e.target.value))}
                          placeholder="Mô tả nhạc bạn muốn tạo, ví dụ: 'Nhạc buồn, piano nhẹ nhàng, tempo chậm khoảng 80 BPM'"
                          disabled={parsingIndex === index}
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          loading={parsingIndex === index}
                          onClick={() => handleParseItemPrompt(index)}
                          style={{ marginTop: 8 }}
                        >
                          Fill with AI
                        </Button>
                      </div>
                    )}

                    {itemMode === 'prompt' && (
                      <div style={{ padding: 12, background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Prompt gửi cho Lyria 3 (chỉnh sửa trực tiếp)</Text>
                        <TextArea
                          size="small"
                          rows={5}
                          value={itemRawPrompts.get(index) || ''}
                          onChange={e => setItemRawPrompts(prev => new Map(prev).set(index, e.target.value))}
                          placeholder="Nhập prompt gửi trực tiếp cho Lyria 3..."
                          style={{ fontSize: 12, fontFamily: 'monospace' }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Name</Text>
                        <Input size="small" value={item.name} onChange={e => updateItem(index, 'name', e.target.value)} disabled={isBasic || isPrompt} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Genre</Text>
                        <Input size="small" value={item.genre} onChange={e => updateItem(index, 'genre', e.target.value)} disabled={isBasic || isPrompt} />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Description</Text>
                      <Input.TextArea size="small" rows={2} value={item.description} onChange={e => updateItem(index, 'description', e.target.value)} disabled={isBasic || isPrompt} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Mood & Tone</Text>
                        <Select size="small" mode="tags" style={{ width: '100%' }} value={item.moodAndTone} onChange={v => updateItem(index, 'moodAndTone', v)} disabled={isBasic || isPrompt} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Tempo (BPM)</Text>
                        <InputNumber size="small" style={{ width: '100%' }} value={item.tempo} onChange={v => updateItem(index, 'tempo', v || 120)} min={40} max={300} disabled={isBasic || isPrompt} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Duration</Text>
                        <Select size="small" style={{ width: '100%' }} value={item.duration} onChange={v => updateItem(index, 'duration', v)} disabled={isBasic || isPrompt}>
                          <Option value={30}>30s (clip)</Option>
                          <Option value={45}>45s</Option>
                          <Option value={60}>1m</Option>
                          <Option value={75}>1m15s</Option>
                          <Option value={90}>1m30s</Option>
                          <Option value={105}>1m45s</Option>
                          <Option value={120}>2m</Option>
                          <Option value={135}>2m15s</Option>
                          <Option value={150}>2m30s</Option>
                          <Option value={165}>2m45s</Option>
                          <Option value={180}>3m</Option>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Instruments</Text>
                      <Select
                        size="small"
                        mode="tags"
                        style={{ width: '100%' }}
                        value={item.instruments}
                        onChange={v => updateItem(index, 'instruments', v)}
                        disabled={isBasic || isPrompt}
                      />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Layering</Text>
                      <TextArea rows={2} value={item.layering} onChange={e => updateItem(index, 'layering', e.target.value)} style={{ fontSize: 12 }} disabled={isBasic || isPrompt} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Flow</Text>
                      <TextArea rows={2} value={item.flow} onChange={e => updateItem(index, 'flow', e.target.value)} style={{ fontSize: 12 }} disabled={isBasic || isPrompt} />
                    </div>
                  </Space>
                </Collapse.Panel>
              );
            })}
          </Collapse>

          <Divider style={{ margin: '12px 0' }} />
          {(() => {
            const savedCount = Array.from(generatedAudios.values()).filter(d => !!d.savedId).length;
            const unsavedCount = generatedAudios.size - savedCount;
            const notGenerated = analysisItems.length - generatedAudios.size;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Button onClick={() => { setAnalysisItems([]); setGeneratedAudios(new Map()); }} disabled={generatingAll || savingAll}>
                    <EditOutlined /> Back to Selection
                  </Button>
                  {generatedAudios.size > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {generatedAudios.size} generated{savedCount > 0 ? ` · ${savedCount} saved` : ''}
                    </Text>
                  )}
                </Space>
                <Space>
                  {unsavedCount > 0 && (
                    <Button
                      icon={<SaveOutlined />}
                      loading={savingAll}
                      disabled={generatingAll || savingIndex !== null}
                      onClick={handleSaveAll}
                    >
                      {savingAll ? 'Saving...' : `Save All (${unsavedCount})`}
                    </Button>
                  )}
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={generatingAll}
                    disabled={generatingIndex !== null || generatedAudios.size === analysisItems.length || savingAll}
                    onClick={handleGenerateAll}
                  >
                    {generatingAll
                      ? `Generating ${generatedAudios.size + 1}/${analysisItems.length}...`
                      : notGenerated > 0 && notGenerated < analysisItems.length
                        ? `Generate Remaining (${notGenerated})`
                        : 'Generate All'}
                  </Button>
                </Space>
              </div>
            );
          })()}
        </div>
      )}
    </Modal>
  );
};

export default GenerateFromFilmModal;
